import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Loader2, Trees, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmt(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

export default function InvoiceView() {
  const { leadId, docId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/invoice/${leadId}/${docId}`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setError("This document was not found. Please call us at (865) 621-0995.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, docId]);

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6 font-body">
        <div className="card-brutal p-10 text-center max-w-md">
          <div className="font-display text-3xl tracking-tight mb-3">Not Found</div>
          <p className="text-forest/80">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center font-body">
        <Loader2 className="w-8 h-8 animate-spin text-safety" />
      </div>
    );
  }

  const kindLabel = (data.kind || "invoice").toLowerCase() === "quote" ? "QUOTE" : "INVOICE";
  const totalLabel = kindLabel === "QUOTE" ? "Estimated Total" : "Amount Due";

  return (
    <div className="min-h-screen bg-bone font-body text-ink py-6 sm:py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="card-brutal overflow-hidden">
          {/* Header */}
          <div className="bg-forest text-bone p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/acorn-logo.jpg"
                alt="Acorn Tree Service²"
                className="h-16 w-auto bg-bone border-2 border-bone"
              />
              <div>
                <div className="font-display text-2xl tracking-wider">{data.business.name}</div>
                <div className="text-xs uppercase tracking-[0.25em] text-bone/70">Kingston, TN</div>
                <a
                  href={`tel:+18656210995`}
                  className="text-safety font-bold mt-1 inline-flex items-center gap-1 text-sm"
                >
                  <Phone className="w-3.5 h-3.5" /> {data.business.phone}
                </a>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <div className="font-display text-4xl sm:text-5xl tracking-[0.15em] text-safety">{kindLabel}</div>
              <div className="text-xs tracking-widest uppercase mt-1">#{data.doc_number}</div>
            </div>
          </div>

          {/* Customer */}
          <div className="p-5 sm:p-6 bg-white">
            <div className="text-xs uppercase tracking-[0.25em] text-safety font-bold mb-1">
              {kindLabel === "INVOICE" ? "Bill To" : "Prepared For"}
            </div>
            <div className="font-display text-2xl tracking-tight">{data.customer.name}</div>
            <div className="text-forest/80 text-sm space-y-1 mt-2">
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> {data.customer.address}</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {data.customer.phone}</div>
              {data.customer.email && (
                <div className="flex items-center gap-2 break-all"><Mail className="w-4 h-4 flex-shrink-0" /> {data.customer.email}</div>
              )}
            </div>

            {/* Items */}
            <div className="mt-6 border-t-2 border-forest pt-4">
              <div className="hidden sm:grid grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.25em] font-bold text-forest/70 pb-3 border-b border-forest/20">
                <div className="col-span-7">Description</div>
                <div className="col-span-1 text-center">Qty</div>
                <div className="col-span-2 text-right">Unit</div>
                <div className="col-span-2 text-right">Total</div>
              </div>
              {data.items.map((it, i) => (
                <div key={i} className="grid sm:grid-cols-12 gap-2 py-3 border-b border-forest/15 text-sm">
                  <div className="sm:col-span-7 font-bold sm:font-normal">{it.description}</div>
                  <div className="sm:col-span-1 text-forest/70 sm:text-center text-xs sm:text-sm">Qty: {it.quantity}</div>
                  <div className="sm:col-span-2 sm:text-right text-forest/70 text-xs sm:text-sm">{fmt(it.unit_price)}</div>
                  <div className="sm:col-span-2 sm:text-right font-bold">{fmt((it.quantity || 1) * (it.unit_price || 0))}</div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{fmt(data.subtotal)}</span></div>
              {data.discount > 0 && (
                <div className="flex justify-between text-safety font-bold">
                  <span>{data.discount_label || "Discount"}</span>
                  <span>−{fmt(data.discount)}</span>
                </div>
              )}
              {data.tax_pct > 0 && (
                <div className="flex justify-between text-forest/80">
                  <span>Tax ({data.tax_pct}%)</span>
                  <span>{fmt(data.tax)}</span>
                </div>
              )}
              <div className="bg-safety text-white px-4 py-3 mt-3 flex items-center justify-between font-display text-2xl tracking-tight border-2 border-forest">
                <span className="uppercase tracking-widest text-base">{totalLabel}</span>
                <span>{fmt(data.total)}</span>
              </div>
            </div>

            {data.note && (
              <div className="mt-5 bg-bone border-l-4 border-safety p-3 text-sm">{data.note}</div>
            )}

            {/* Accept */}
            <div className="mt-8 border-t-2 border-forest pt-5">
              <div className="text-xs uppercase tracking-[0.25em] text-safety font-bold mb-3">
                {kindLabel === "QUOTE" ? "Ready to schedule?" : "Questions?"}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <a
                  href={`tel:+18656210995`}
                  className="btn-primary px-5 py-4 text-lg flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" strokeWidth={3} /> CALL (865) 621-0995
                </a>
                <a
                  href={`sms:+18656210995?&body=${encodeURIComponent(
                    `Hi, I'd like to accept ${kindLabel.toLowerCase()} #${data.doc_number}.`,
                  )}`}
                  className="btn-dark px-5 py-4 text-lg flex items-center justify-center gap-2"
                  style={{ background: "#1B2A22", color: "#F4F1ED" }}
                >
                  <CheckCircle2 className="w-5 h-5" strokeWidth={3} /> TEXT TO ACCEPT
                </a>
              </div>
              <div className="text-xs uppercase tracking-widest text-forest/60 mt-4 text-center">
                {kindLabel === "QUOTE" ? "Estimate valid 30 days" : "Thanks for your business"} ·
                Insured · Locally Owned
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 text-xs uppercase tracking-[0.25em] text-forest/60">
          <Trees className="w-4 h-4 inline mr-1 text-safety" /> Powered by Acorn Tree Service²
        </div>
      </div>
    </div>
  );
}
